import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DollarSign, TrendingUp, Users, Package, ArrowLeft, Percent,
  BarChart3, PieChart, Award, Handshake, Scale, Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierPerformance() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || user?.role !== 'admin') return;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/supplier-performance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        console.error('Failed to load supplier performance:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user]);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading supplier performance...</div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const topSuppliers = data?.top_suppliers || [];
  const productPricing = data?.product_pricing || [];
  const monthlyTrends = data?.monthly_trends || [];
  const agreements = data?.agreements || {};

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="supplier-performance-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground" data-testid="dashboard-title">
                Supplier Performance
              </h1>
              <p className="text-muted-foreground text-sm mt-1">Dual markup analytics & supplier insights</p>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="kpi-cards">
          <Card className="bg-muted/60 border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Package className="h-3.5 w-3.5" /> Products
              </div>
              <p className="text-2xl font-bold text-foreground" data-testid="total-products">{summary.total_products}</p>
              <p className="text-xs text-muted-foreground mt-1">Active in catalog</p>
            </CardContent>
          </Card>

          <Card className="bg-muted/60 border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-emerald-400 text-xs mb-1">
                <DollarSign className="h-3.5 w-3.5" /> Platform Margin
              </div>
              <p className="text-2xl font-bold text-emerald-400" data-testid="platform-margin">
                ${summary.platform_margin?.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Commission + Markup per unit</p>
            </CardContent>
          </Card>

          <Card className="bg-muted/60 border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-400 text-xs mb-1">
                <Percent className="h-3.5 w-3.5" /> Avg Commission
              </div>
              <p className="text-2xl font-bold text-amber-400" data-testid="avg-commission">
                {(summary.avg_commission_rate * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Supplier fee rate</p>
            </CardContent>
          </Card>

          <Card className="bg-muted/60 border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-indigo-400 text-xs mb-1">
                <TrendingUp className="h-3.5 w-3.5" /> Avg Markup
              </div>
              <p className="text-2xl font-bold text-indigo-400" data-testid="avg-markup">
                {(summary.avg_markup_rate * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Customer markup rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Breakdown + Agreements */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-muted/60 border-border md:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-indigo-400" /> Revenue Breakdown (Per-Unit)
              </CardTitle>
            </CardHeader>
            <CardContent data-testid="revenue-breakdown">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Total Base (Wholesale)</p>
                    <p className="text-xl font-bold text-foreground">${summary.total_base_revenue?.toFixed(2)}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Customer Revenue</p>
                    <p className="text-xl font-bold text-emerald-400">${summary.total_customer_revenue?.toFixed(2)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                    <p className="text-xs text-amber-400 mb-1">Supplier Commission Earned</p>
                    <p className="text-xl font-bold text-amber-400">${summary.total_commission_revenue?.toFixed(2)}</p>
                  </div>
                  <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
                    <p className="text-xs text-indigo-400 mb-1">Customer Markup Earned</p>
                    <p className="text-xl font-bold text-indigo-400">${summary.total_markup_revenue?.toFixed(2)}</p>
                  </div>
                </div>
                {/* Visual bar */}
                <div className="mt-2">
                  <div className="flex h-6 rounded-full overflow-hidden bg-muted">
                    {summary.total_customer_revenue > 0 && (
                      <>
                        <div
                          className="bg-slate-500 flex items-center justify-center text-[10px] text-foreground font-medium"
                          style={{ width: `${((summary.total_base_revenue - summary.total_commission_revenue) / summary.total_customer_revenue) * 100}%` }}
                        >
                          Supplier Net
                        </div>
                        <div
                          className="bg-amber-500 flex items-center justify-center text-[10px] text-foreground font-medium"
                          style={{ width: `${(summary.total_commission_revenue / summary.total_customer_revenue) * 100}%` }}
                        >
                          Comm
                        </div>
                        <div
                          className="bg-indigo-500 flex items-center justify-center text-[10px] text-foreground font-medium"
                          style={{ width: `${(summary.total_markup_revenue / summary.total_customer_revenue) * 100}%` }}
                        >
                          Markup
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-muted/60 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Handshake className="h-4 w-4 text-emerald-400" /> Agreements
              </CardTitle>
            </CardHeader>
            <CardContent data-testid="agreements-summary">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="text-lg font-bold text-foreground">{agreements.total}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-emerald-400">Active</span>
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">{agreements.active}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-amber-400">Pending</span>
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{agreements.pending}</Badge>
                </div>
              </div>

              {/* Top Suppliers */}
              {topSuppliers.length > 0 && (
                <div className="mt-6 pt-4 border-t border-border">
                  <h4 className="text-xs text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1">
                    <Award className="h-3 w-3" /> Top Suppliers
                  </h4>
                  <div className="space-y-2">
                    {topSuppliers.map((s, i) => (
                      <div key={s.vendor_id} className="flex items-center justify-between" data-testid={`top-supplier-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                          <span className="text-sm text-foreground truncate max-w-[120px]">{s.vendor_name}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">{s.agreement_count} deals</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trends */}
        {monthlyTrends.length > 0 && (
          <Card className="bg-muted/60 border-border mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-400" /> Monthly Revenue Trends
              </CardTitle>
            </CardHeader>
            <CardContent data-testid="monthly-trends">
              <div className="flex items-end gap-2 h-32">
                {monthlyTrends.map((m, i) => {
                  const maxRev = Math.max(...monthlyTrends.map(t => t.revenue || 1));
                  const height = Math.max(((m.revenue || 0) / maxRev) * 100, 4);
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-emerald-400">${(m.revenue || 0).toFixed(0)}</span>
                      <div
                        className="w-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-sm"
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-[10px] text-muted-foreground">{m.month.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Product Pricing Table */}
        <Card className="bg-muted/60 border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-foreground flex items-center gap-2">
              <PieChart className="h-4 w-4 text-indigo-400" /> Product Pricing Overview
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="product-pricing-table">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground text-xs">Product</TableHead>
                    <TableHead className="text-muted-foreground text-xs">Category</TableHead>
                    <TableHead className="text-muted-foreground text-xs text-right">Base Price</TableHead>
                    <TableHead className="text-muted-foreground text-xs text-right">Commission</TableHead>
                    <TableHead className="text-muted-foreground text-xs text-right">Supplier Net</TableHead>
                    <TableHead className="text-muted-foreground text-xs text-right">Markup</TableHead>
                    <TableHead className="text-muted-foreground text-xs text-right">Customer Price</TableHead>
                    <TableHead className="text-muted-foreground text-xs text-right">Platform Rev</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productPricing.map((p) => (
                    <TableRow key={p.service_id} className="border-border/50 hover:bg-muted/30">
                      <TableCell className="text-sm text-foreground font-medium max-w-[200px] truncate">
                        {p.name}
                        {p.pricing_unit_label && (
                          <span className="ml-1 text-xs text-muted-foreground">/{p.pricing_unit_label}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{p.category}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">${p.base_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right text-sm text-amber-400">
                        {(p.supplier_commission_rate * 100).toFixed(0)}%
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">${p.supplier_net.toFixed(2)}</TableCell>
                      <TableCell className="text-right text-sm text-indigo-400">
                        {(p.customer_markup_rate * 100).toFixed(0)}%
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold text-emerald-400">
                        ${p.customer_price.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold text-foreground">
                        ${p.platform_revenue.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {productPricing.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No products with pricing data yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
